import { useMemo, useState } from 'react';
import type {
  DemographicsDataAdapter
} from '@/types/demographics';
import {
  processAgeDistributionData,
  processGenderDistributionData
} from '@/utils/demographics';

// Generic hook that accepts any data source and adapter
export const useDemographicsProps = <T>(
  dataSource: T | null | undefined,
  adapter: DemographicsDataAdapter<T>,
  isLoading: boolean,
  defaultTab: string = 'age',
  layout: 'side-by-side' | 'stacked' = 'side-by-side',
) => {
  const [activeDemographicTab, setActiveDemographicTab] = useState<string>(defaultTab);

  // Extract demographics data using adapter
  const demographicsData = useMemo(() => {
    if (!dataSource) return null;
    return adapter.extractDemographicsData(dataSource);
  }, [dataSource, adapter]);

  // Get display configuration from adapter
  const displayConfig = useMemo(() => {
    return adapter.getDisplayConfig();
  }, [adapter]);

  // Extract summary data
  const summary = useMemo(() => {
    if (!dataSource) {
      return { ageBucket: '--', gender: '--', country: '--', city: '--' };
    }
    return adapter.extractSummary(dataSource);
  }, [dataSource, adapter]);

  // Process age distribution data
  const ageDistributionData = useMemo(() => {
    if (!demographicsData?.ageDistribution) return [];
    return processAgeDistributionData(demographicsData.ageDistribution, displayConfig);
  }, [demographicsData?.ageDistribution, displayConfig]);

  // Process gender distribution data
  const genderDistributionData = useMemo(() => {
    if (!demographicsData?.genderDistribution) return [];
    return processGenderDistributionData(demographicsData.genderDistribution, displayConfig);
  }, [demographicsData?.genderDistribution, displayConfig]);

  // Process location data
  const { topCountriesData, topCitiesData } = useMemo(() => {
    try {
      return {
        topCountriesData: demographicsData?.topCountries || [],
        topCitiesData: demographicsData?.topCities || [],
      };
    } catch (error) {
      console.error('Error processing location data:', error);
      return { topCountriesData: [], topCitiesData: [] };
    }
  }, [demographicsData?.topCountries, demographicsData?.topCities]);

  return {
    activeDemographicTab,
    setActiveDemographicTab,
    isLoading,
    summary,
    ageDistributionData,
    genderDistributionData,
    topCountriesData,
    topCitiesData,
    layout,
  };
};
